import React from 'react';
import { Button } from './ui/button';
import { 
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  MessageCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const sitemapSections = [
    {
      title: "Services",
      links: [
        { name: "Window Shutters", path: "/shutters" },
        { name: "Use Our Online Estimator", path: "/calculator" },
        { name: "Book Consultation", path: "/contact" }
      ]
    },
    {
      title: "Shutters",
      links: [
        { name: "Shutter Styles", path: "/shutters/styles" },
        { name: "Materials", path: "/shutters/materials" },
        { name: "Louvre Sizes", path: "/shutters/louvre-sizes" },
        { name: "Room Solutions", path: "/shutters/rooms" }
      ]
    },
    {
      title: "Company",
      links: [
        { name: "About Us", path: "/about" },
        { name: "Contact", path: "/contact" },
        { name: "Privacy Policy", path: "/privacy-policy" },
        { name: "Terms of Service", path: "/terms-of-service" }
      ]
    }
  ];

  const serviceAreas = [
    "Bristol", "Somerset", "Gloucestershire", 
    "Wiltshire", "South Wales", "Bath", 
    "Exeter", "Gloucester", "Swindon"
  ];

  return (
    <footer className="bg-slate-850 text-white font-body">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Company Info */}
          <div className="lg:col-span-4 space-y-6">
            <div>
              <h3 className="font-display text-2xl text-white mb-4">Aston Lily Shutters</h3>
              <p className="text-stone-400 leading-relaxed text-sm">
                Family owned supplier and installer of made to measure shutters across Bristol, 
                Somerset, Gloucestershire, Wiltshire & South Wales.
              </p>
            </div>

            {/* Contact Info */}
            <div className="space-y-3">
              <a href="https://wa.me/447378415338" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-stone-300 hover:text-green-400 transition-colors">
                <MessageCircle className="h-4 w-4 text-green-400" />
                <span className="text-sm">WhatsApp Us</span>
              </a>
              <a href="mailto:orders@astonlily.co.uk" className="flex items-center gap-3 text-stone-300 hover:text-white transition-colors">
                <Mail className="h-4 w-4 text-sage-400" />
                <span className="text-sm">orders@astonlily.co.uk</span>
              </a>
              <div className="flex items-start gap-3 text-stone-300">
                <MapPin className="h-4 w-4 text-sage-400 mt-0.5" />
                <span className="text-sm">Bristol & South West</span>
              </div>
            </div>

            {/* CTA */}
            <div className="pt-4">
              <Link to="/calculator">
                <Button className="bg-sage-400 hover:bg-sage-500 text-white rounded-full px-6 py-5 text-sm font-medium w-full sm:w-auto">
                  Use Our Online Estimator
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Sitemap */}
          <div className="lg:col-span-5 grid grid-cols-3 gap-8">
            {sitemapSections.map((section, index) => (
              <div key={index}>
                <h4 className="text-xs font-medium tracking-widest uppercase text-stone-500 mb-4">
                  {section.title}
                </h4>
                <ul className="space-y-2.5">
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      <Link 
                        to={link.path}
                        className="text-stone-300 hover:text-white text-sm transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Service Areas */}
          <div className="lg:col-span-3">
            <h4 className="text-xs font-medium tracking-widest uppercase text-stone-500 mb-4">
              Service Areas
            </h4>
            <div className="flex flex-wrap gap-2">
              {serviceAreas.map((area, index) => (
                <span 
                  key={index}
                  className="text-xs text-stone-400 bg-white/5 px-3 py-1.5 rounded-full"
                >
                  {area}
                </span>
              ))}
            </div>
            
            {/* WhatsApp */}
            <div className="mt-8">
              <a 
                href="https://wa.me/447378415338" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sage-400 hover:text-sage-300 transition-colors"
              >
                <MessageCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Chat on WhatsApp</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex flex-wrap items-center gap-6 text-xs text-stone-500">
              <span>© {currentYear} Aston Lily Shutters</span>
              <span>Family Business Since 2018</span>
              <span>10 Year Structural Warranty</span>
            </div>
            <div className="flex items-center gap-6 text-xs text-stone-500">
              <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/terms-of-service" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
